import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { DollarSign } from 'lucide-react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Cell, ZAxis, Legend,
  BarChart, Bar,
} from 'recharts';

const SCATTER_DATA = [
  { name: 'Coop. Salute Roma', costo: 3200, esito: 88, pazienti: 186 },
  { name: 'Consorzio ADI Lazio', costo: 2900, esito: 92, pazienti: 234 },
  { name: 'Gemelli@Home', costo: 3800, esito: 78, pazienti: 142 },
  { name: 'ASL Roma 2', costo: 4100, esito: 65, pazienti: 98 },
  { name: 'Coop. Riabilita', costo: 3100, esito: 85, pazienti: 156 },
];

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const SAVINGS = [
  { label: 'Ricoveri Evitati (stima)', count: 142, costoMedio: '€ 4.800', totale: '€ 681.600' },
  { label: 'Accessi PS Evitati', count: 318, costoMedio: '€ 280', totale: '€ 89.040' },
  { label: 'Giornate Degenza Risparmiate', count: 1240, costoMedio: '€ 650', totale: '€ 806.000' },
];

const ESITI_DATA = [
  { nome: 'Stabilizzazione clinica', percentuale: 78 },
  { nome: 'Miglioramento funzionale', percentuale: 62 },
  { nome: 'Guarigione ferite', percentuale: 85 },
  { nome: 'Aderenza terapeutica', percentuale: 91 },
  { nome: 'Soddisfazione pz.', percentuale: 88 },
];

const ESITI_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#14b8a6'];

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const quadrante = d.costo <= 3400 && d.esito >= 80 ? 'Quadrante ideale' : d.costo > 3400 && d.esito < 80 ? 'Area critica' : 'Area intermedia';
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-xs">
      <p className="font-bold text-slate-800 dark:text-white mb-1">{d.name}</p>
      <p className="text-slate-500">Costo/pz: <strong>€ {d.costo.toLocaleString()}</strong></p>
      <p className="text-slate-500">Esito: <strong>{d.esito}/100</strong></p>
      <p className="text-slate-500">Pazienti: <strong>{d.pazienti}</strong></p>
      <p className="text-slate-400 text-[10px] mt-1 pt-1 border-t border-slate-100 dark:border-slate-800">{quadrante}</p>
    </div>
  );
}

function EsitiTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 text-xs">
      <p className="font-bold text-slate-800 dark:text-white mb-1">{d.nome}</p>
      <p className="text-slate-500">Raggiungimento: <strong>{d.percentuale}%</strong></p>
    </div>
  );
}

export default function ASLValore() {
  return (
    <AppLayout role="asl">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="asl-valore-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Valore ed Esiti</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Value-Based Healthcare — Costi evitati e matrice efficienza</p>
        </div>

        {/* Savings Counter */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white shadow-lg" data-testid="savings-counter">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-emerald-200" />
            <h2 className="text-sm font-bold">Costi Evitati — Stima Anno Corrente</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {SAVINGS.map(s => (
              <div key={s.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-[10px] text-emerald-200 uppercase tracking-wider mb-1">{s.label}</p>
                <p className="text-2xl font-bold">{s.totale}</p>
                <p className="text-[10px] text-emerald-200 mt-1">{s.count} eventi x {s.costoMedio} DRG medio</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
            <p className="text-sm">Risparmio Totale Stimato</p>
            <p className="text-2xl font-bold">€ 1.576.640</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Scatter Plot with Cell colors */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="scatter-plot">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-slate-800 dark:text-white">Matrice Efficienza — Costo vs Esito</h2>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">Ogni punto rappresenta un erogatore</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" />
                  <XAxis dataKey="costo" name="Costo/Pz" tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'Costo per Paziente (€)', position: 'insideBottom', offset: -5, style: { fontSize: 10, fill: '#94a3b8' } }} />
                  <YAxis dataKey="esito" name="Esito" tick={{ fontSize: 10, fill: '#94a3b8' }} label={{ value: 'Esito Clinico (0-100)', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#94a3b8' } }} />
                  <ZAxis dataKey="pazienti" range={[80, 300]} name="Pazienti" />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine x={3400} stroke="#e2e8f0" strokeDasharray="3 3" label={{ value: 'Soglia Costo', position: 'top', style: { fontSize: 9, fill: '#94a3b8' } }} />
                  <ReferenceLine y={80} stroke="#e2e8f0" strokeDasharray="3 3" label={{ value: 'Soglia Esito', position: 'right', style: { fontSize: 9, fill: '#94a3b8' } }} />
                  <Scatter data={SCATTER_DATA} name="Erogatori">
                    {SCATTER_DATA.map((entry, index) => (
                      <Cell key={`scatter-${index}`} fill={COLORS[index]} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              {SCATTER_DATA.map((d, i) => (
                <span key={d.name} className="flex items-center gap-1.5 text-[10px] text-slate-600 dark:text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i] }} /> {d.name}
                </span>
              ))}
            </div>
          </div>

          {/* Esiti Clinici — Horizontal Bars */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6" data-testid="esiti-chart">
            <h2 className="text-sm font-bold text-slate-800 dark:text-white mb-1">Esiti Clinici Aggregati</h2>
            <p className="text-[10px] text-slate-400 mb-4">% raggiungimento obiettivi PAI per tipologia</p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ESITI_DATA} layout="vertical" margin={{ top: 0, right: 20, left: 15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} unit="%" />
                  <YAxis dataKey="nome" type="category" tick={{ fontSize: 10, fill: '#94a3b8' }} width={130} />
                  <Tooltip content={<EsitiTooltip />} />
                  <Bar dataKey="percentuale" name="Raggiungimento %" radius={[0, 6, 6, 0]} barSize={18}>
                    {ESITI_DATA.map((entry, index) => (
                      <Cell key={`esiti-${index}`} fill={ESITI_COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              {ESITI_DATA.map((d, i) => (
                <span key={d.nome} className="flex items-center gap-1.5 text-[10px] text-slate-600 dark:text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ESITI_COLORS[i] }} /> {d.nome}: {d.percentuale}%
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
